import { queryOptions } from '@tanstack/react-query';

import { queryClient } from '@/services/query-client';

import { fetchCustomers } from '../data-provider';
import type { Customer, CustomerFilters, Customers } from '../models';
import { CACHE_KEYS } from '../types';
import { logger } from '../utils/logger';

export const customersQueryOptions = (
  filters: Partial<CustomerFilters> = {},
  { enabled = true }: { enabled?: boolean } = {},
) =>
  queryOptions({
    enabled,
    queryFn: async ({ signal }) => {
      const customers = await fetchCustomers(filters, { signal });
      put(customers.list);
      return customers;
    },
    queryKey: CACHE_KEYS.customers(filters),
    // biome-ignore lint/style/noMagicNumbers: 5 minutes
    staleTime: 1000 * 60 * 5,
  });

const INTERNAL_CUSTOMERS_CACHE_KEY = btoa('internal-customers-cache-key');

const INTERNAL_QUERY_CUSTOMERS_KEY = customersQueryOptions({ name: INTERNAL_CUSTOMERS_CACHE_KEY }).queryKey;

const getCustomerFallback = (customerId: string): Customer => {
  logger.warn(`Customer "${customerId}" was not found. Verify the data model.`);
  return {
    id: customerId,
    label: customerId,
    name: customerId,
  };
};

export const put = async (customers: Customer[]) => {
  const data = queryClient.getQueryData(INTERNAL_QUERY_CUSTOMERS_KEY);

  const newCustomers = customers.reduce(
    (acc, customer) => {
      acc[String(customer.id)] = customer;
      return acc;
    },
    { ...(data?.map ?? {}) },
  );

  queryClient.setQueryData(INTERNAL_QUERY_CUSTOMERS_KEY, {
    list: Object.values(newCustomers),
    map: newCustomers,
  });
};

export const getAll = (): Customers | undefined => {
  return queryClient.getQueryData(INTERNAL_QUERY_CUSTOMERS_KEY);
};

export const find = async (customerId: string): Promise<Customer> => {
  const data = queryClient.getQueryData(INTERNAL_QUERY_CUSTOMERS_KEY);

  const customer = data?.map[customerId];
  if (customer) {
    return customer;
  }

  try {
    const newData = await queryClient.query(customersQueryOptions({ search: { id: customerId } }));
    if (newData.map[customerId]) {
      void put(newData.list);
      return newData.map[customerId];
    }
  } catch {
    return getCustomerFallback(customerId);
  }

  return getCustomerFallback(customerId);
};

export const findAll = async (customerIds: string[]): Promise<Customer[]> => {
  const data = queryClient.getQueryData(INTERNAL_QUERY_CUSTOMERS_KEY);

  const missingIds: string[] = [];
  const customers: Customer[] = [];

  for (const customerId of customerIds) {
    const c = data?.map[customerId];
    if (c) {
      customers.push(c);
    } else {
      missingIds.push(customerId);
    }
  }

  if (!missingIds.length) {
    return customers;
  }

  try {
    const newData = await queryClient.query(customersQueryOptions({ search: { id: missingIds.join(',') } }));
    void put(newData.list);
    for (const missingId of missingIds) {
      const c = newData.map[missingId];
      if (c) {
        customers.push(c);
      } else {
        customers.push(getCustomerFallback(missingId));
      }
    }
  } catch {
    for (const missingId of missingIds) {
      customers.push(getCustomerFallback(missingId));
    }
  }

  return customers;
};
